import type { ReactElement } from 'react';
import { useState } from 'react';
import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
// @ts-expect-error
import type CardElement from '@adyen/adyen-web/dist/types/components/Card';
import { ZodError } from 'zod';
import {
    BANNERS,
    useAlertBanner,
    useUserSession,
    logSentryError,
    SentryAcpCreditCardsErrors as SentryErrors,
} from '@libs/common-next';
import type { AddPaymentFormInterface } from './schema';
import { paymentFormSchema } from './schema';
import type { AddressFormType } from '../AddressForm/Address.schema';
import { AdyenForm } from '../AdyenForm';
import type { ExperianResponseLayout, FormProps } from '../ExperianModal';
import { ExperianModal, validateExperianAddressLayout } from '../ExperianModal';
import { BillingAddress } from '../BillingAddress';
import type { CardState } from '../../hooks/types';
import { addPayment, validateAddressForm } from './utils';
import {
    CARD_REJECTED_MESSAGE,
    CARD_TOKENIZATION_REJECTED_MESSAGE,
    PAYMENT_PROCESSOR_ERROR_MESSAGE,
} from './constants';
import { PROFILE_DATA } from '../../constants';
import { IsDefaultCheckbox } from './IsDefaultCheckbox';

const DEFAULT_ADDRESS_FORM_STATE: AddressFormType = {
    apt: '',
    city: '',
    state: '',
    streetAddress: '',
    zipCode: '',
};

function getDefaultPaymentFormState(isPrimary = false): AddPaymentFormInterface {
    return {
        address: DEFAULT_ADDRESS_FORM_STATE,
        addressType: 'form',
        firstName: '',
        lastName: '',
        isPrimary,
        phone: '',
    };
}

export type AddPaymentForm = {
    closeForm: () => void;
    defaultAddressId?: string;
    isDefaultCheckChecked: boolean;
    isDefaultCheckDisabled: boolean;
};

export function AddPaymentForm({
    closeForm,
    defaultAddressId,
    isDefaultCheckChecked,
    isDefaultCheckDisabled,
}: Readonly<AddPaymentForm>): ReactElement {
    const { updateProfileData } = useUserSession();

    const [adyenForm, setAdyenForm] = useState<CardElement>();
    const [adyenFormState, setAdyenFormState] = useState<CardState>();
    const [isExperianLoading, setIsExperianLoading] = useState(false);
    const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { setAlert } = useAlertBanner();

    const defaultPayment = getDefaultPaymentFormState(isDefaultCheckChecked);
    const paymentFormState: AddPaymentFormInterface = defaultAddressId
        ? {
              ...defaultPayment,
              address: undefined,
              addressType: 'id',
              firstName: undefined,
              lastName: undefined,
              isPrimary: isDefaultCheckChecked,
              phone: undefined,
              selectedAddressId: defaultAddressId.toString(),
          }
        : defaultPayment;

    const validate = async (values: AddPaymentFormInterface) => {
        let errors: FormikErrors<AddPaymentFormInterface> = {};

        try {
            paymentFormSchema.parse(values);
        } catch (error) {
            if (error instanceof ZodError) {
                errors = error.formErrors.fieldErrors;
            }
        }

        if (values.address) {
            const addressErrors = await validateAddressForm(values.address);

            if (addressErrors) {
                errors.address = addressErrors as FormikErrors<AddPaymentFormInterface> as string;
            }
        }

        return Object.keys(errors) ? errors : true;
    };

    const handleErrorBanner = (errors: FormikErrors<AddPaymentFormInterface>) => {
        const hasFormErrors = Object.keys(errors).length > 0;
        const isAdyenInvalid = !adyenFormState?.isValid;

        if (isAdyenInvalid) {
            adyenForm?.showValidation();
        }

        if (hasFormErrors) {
            setAlert({ ...BANNERS.invalidPaymentMethodFormik });
        } else if (isAdyenInvalid) {
            setAlert({ ...BANNERS.invalidPaymentMethodAdyen });
        }
    };

    /**
     * Final function will be invoked after the experian address validation
     * if there are modals opened, will be invoked after clicking CTA on the modal
     * if there are no modals opened, will be called at once with Save cta click on a modal
     */
    const handleSubmitForm = async (values: AddPaymentFormInterface) => {
        if (!adyenFormState?.isValid) {
            setAlert({ ...BANNERS.invalidPaymentMethodAdyen });
            adyenForm?.showValidation();
            return;
        }

        setAlert(null);
        setIsSubmitting(true);

        try {
            const response = await addPayment(adyenFormState, values);

            if (
                response &&
                'message' in response &&
                [CARD_TOKENIZATION_REJECTED_MESSAGE, CARD_REJECTED_MESSAGE, PAYMENT_PROCESSOR_ERROR_MESSAGE].includes(
                    response.message as string
                )
            ) {
                setAlert({ ...BANNERS.invalidPaymentMethodAdyen });
            } else if (response && 'payment' in response) {
                await updateProfileData?.(PROFILE_DATA.ADDRESSES);

                closeForm();
                setAlert({ ...BANNERS.success, skipScrollToAlert: true });

                await updateProfileData?.(PROFILE_DATA.PAYMENTS);

                return;
            }
        } catch (error) {
            logSentryError(error, SentryErrors.AddPaymentForm.SubmitFailed);
        } finally {
            setIsSubmitting(false);
        }
    };
    /** Should be called on Save CTA click */
    const validateExperianOnSubmit = async (values: AddPaymentFormInterface) => {
        if (!adyenFormState?.isValid) {
            return;
        }
        if (values.addressType === 'id') {
            handleSubmitForm(values);
        } else {
            setAlert(null);
            const addressValues = values.address;
            setIsExperianLoading(true);
            const experianResponseValue = await validateExperianAddressLayout(addressValues);

            setExperianResponse(experianResponseValue);
            setIsExperianLoading(false);
        }
    };

    return (
        <Formik
            initialValues={paymentFormState}
            validate={validate}
            validateOnChange={true}
            onSubmit={validateExperianOnSubmit}
        >
            {formikProps => (
                <Form noValidate onSubmit={formikProps.handleSubmit} data-testid="add-billing-address-form">
                    <AdyenForm setAdyenForm={setAdyenForm} setAdyenFormState={setAdyenFormState} />
                    <ExperianModal
                        experianResponse={experianResponse}
                        saveSelectedAddress={(formProps: FormProps) =>
                            handleSubmitForm(formProps as AddPaymentFormInterface)
                        }
                        loading={isExperianLoading}
                        setIsLoading={setIsExperianLoading}
                    />

                    <IsDefaultCheckbox disabled={isDefaultCheckDisabled} />

                    <BillingAddress
                        onCancel={closeForm}
                        defaultAddressId={defaultAddressId}
                        isLoading={isExperianLoading || isSubmitting}
                        handleErrorBanner={handleErrorBanner}
                    />
                </Form>
            )}
        </Formik>
    );
}
